import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { leadsAPI } from '@/lib/api';
import { useSettings } from '@/context/SettingsContext';
import { Plus, Search, Filter, Calendar, DollarSign, Mail, Phone, Eye, Package, Trash2, ShoppingCart, Percent, Truck, Image, User, X, MapPin, RefreshCw, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { getLeadStageColor, formatDate } from '@/lib/utils';

const ITEMS_PER_PAGE = 15;

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LEAD_STAGES = [
  { id: 'new', label: 'New', color: 'bg-blue-500' },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
  { id: 'quotation_sent', label: 'Quotation Sent', color: 'bg-purple-500' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
  { id: 'closed_won', label: 'Closed Won', color: 'bg-green-500' },
  { id: 'closed_lost', label: 'Closed Lost', color: 'bg-red-500' },
];

const CreateLeadDialog = ({ onSuccess }) => {
  const { formatCurrency } = useSettings();
  const [open, setOpen] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [searchProduct, setSearchProduct] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: '',
    stage: 'new',
    source: '',
    estimated_value: '',
    notes: '',
    products: [],
    shipping_charge: 0,
    discount_type: '',
    discount_value: 0,
    custom_charges: [],
    customer_id: null,
    shipping_address: null,
    billing_address: null
  });
  const [newCustomCharge, setNewCustomCharge] = useState({ name: '', amount: 0 });
  
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Address states
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
  });
  const [sameAsShipping, setSameAsShipping] = useState(true);

  // Search customers
  useEffect(() => {
    const searchCustomersAPI = async () => {
      if (!customerSearch || customerSearch.length < 2) {
        setCustomerResults([]);
        return;
      }
      
      setSearchingCustomers(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/customers`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: customerSearch, limit: 10 }
        });
        setCustomerResults(response.data.customers || []);
      } catch (error) {
        console.error('Customer search error:', error);
      } finally {
        setSearchingCustomers(false);
      }
    };
    
    const timeoutId = setTimeout(searchCustomersAPI, 300);
    return () => clearTimeout(timeoutId);
  }, [customerSearch]);

  // Select customer and pre-fill fields
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customer_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.customer_name || '',
      email: customer.email || '',
      phone: customer.phone || customer.default_address?.phone || '',
      customer_id: customer.id
    }));
    
    // Pre-fill address if available
    if (customer.default_address) {
      const addr = customer.default_address;
      setShippingAddress({
        address1: addr.address1 || '',
        address2: addr.address2 || '',
        city: addr.city || '',
        province: addr.province || '',
        country: addr.country || 'UAE',
        zip: addr.zip || '',
        phone: addr.phone || customer.phone || ''
      });
      setShowAddressFields(true);
    }
    
    setCustomerSearch('');
    setCustomerResults([]);
    toast.success(`Customer "${customer.first_name || customer.customer_name}" selected`);
  };

  // Clear selected customer
  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setFormData(prev => ({
      ...prev,
      customer_id: null
    }));
  };

  // Fetch inventory when dialog opens
  useEffect(() => {
    if (open) {
      fetchInventory();
    }
  }, [open]);

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 500 }
      });
      setInventory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
    }
  };

  const addProductToLead = (item) => {
    const existingIndex = formData.products.findIndex(p => p.product_id === item.id);
    if (existingIndex >= 0) {
      const updated = [...formData.products];
      updated[existingIndex].quantity += 1;
      updated[existingIndex].total = 
        (updated[existingIndex].unit_price * updated[existingIndex].quantity) + 
        (updated[existingIndex].customization_charge || 0);
      setFormData({ ...formData, products: updated });
    } else {
      const newProduct = {
        product_id: item.id,
        sku: item.sku,
        name: item.name,
        quantity: 1,
        unit_price: item.selling_price || 0,
        customization_charge: 0,
        total: item.selling_price || 0,
        image_url: item.image_url
      };
      setFormData({ ...formData, products: [...formData.products, newProduct] });
    }
    setSearchProduct('');
  };

  const updateProduct = (index, field, value) => {
    const updated = [...formData.products];
    updated[index][field] = value;
    updated[index].total = 
      (updated[index].unit_price * updated[index].quantity) + 
      (updated[index].customization_charge || 0);
    setFormData({ ...formData, products: updated });
  };

  const removeProduct = (index) => {
    setFormData({ ...formData, products: formData.products.filter((_, i) => i !== index) });
  };

  const addCustomCharge = () => {
    if (newCustomCharge.name && newCustomCharge.amount > 0) {
      setFormData({
        ...formData,
        custom_charges: [...formData.custom_charges, { ...newCustomCharge }]
      });
      setNewCustomCharge({ name: '', amount: 0 });
    }
  };

  const removeCustomCharge = (index) => {
    setFormData({
      ...formData,
      custom_charges: formData.custom_charges.filter((_, i) => i !== index)
    });
  };

  // Calculate totals
  const totalProductValue = formData.products.reduce((sum, p) => sum + (p.total || 0), 0);
  const shippingCharge = parseFloat(formData.shipping_charge) || 0;
  const customChargesTotal = formData.custom_charges.reduce((sum, c) => sum + (c.amount || 0), 0);
  const subtotal = totalProductValue + shippingCharge + customChargesTotal;
  let discountAmount = 0;
  if (formData.discount_type === 'percentage' && formData.discount_value > 0) {
    discountAmount = (subtotal * formData.discount_value) / 100;
  } else if (formData.discount_type === 'fixed' && formData.discount_value > 0) {
    discountAmount = parseFloat(formData.discount_value) || 0;
  }
  const grandTotal = subtotal - discountAmount;

  const filteredInventory = inventory.filter(
    (item) =>
      (item.name || '').toLowerCase().includes(searchProduct.toLowerCase()) ||
      (item.sku || '').toLowerCase().includes(searchProduct.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate final value
      const finalValue = grandTotal > 0 ? grandTotal : (parseFloat(formData.estimated_value) || 0);
      
      // Build shipping address if provided
      const shipAddr = showAddressFields && shippingAddress.address1 ? shippingAddress : null;
      const billAddr = showAddressFields && !sameAsShipping && billingAddress.address1 ? billingAddress : shipAddr;
      
      await leadsAPI.create({
        ...formData,
        estimated_value: finalValue,
        shipping_address: shipAddr,
        billing_address: billAddr,
        billing_same_as_shipping: sameAsShipping
      });
      toast.success('Lead created successfully');
      setOpen(false);
      setFormData({
        customer_name: '',
        email: '',
        phone: '',
        stage: 'new',
        source: '',
        estimated_value: '',
        notes: '',
        products: [],
        shipping_charge: 0,
        discount_type: '',
        discount_value: 0,
        custom_charges: [],
        customer_id: null,
        shipping_address: null,
        billing_address: null
      });
      setSelectedCustomer(null);
      setShowAddressFields(false);
      setShippingAddress({
        address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
      });
      setBillingAddress({
        address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
      });
      setSameAsShipping(true);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-wt-gold text-black hover:bg-wt-gold-hover" data-testid="new-lead-button">
          <Plus className="mr-2 h-4 w-4" />
          New Lead
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-wt-text-primary">
            Create New Lead
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Search Section */}
          <div className="border border-wt-border rounded-lg p-4 space-y-3">
            <Label className="text-wt-text-primary flex items-center gap-2">
              <User className="h-4 w-4" />
              Customer (Search existing or enter new)
            </Label>
            
            {selectedCustomer ? (
              <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div>
                  <p className="text-green-400 font-medium">{formData.customer_name}</p>
                  <p className="text-xs text-wt-text-muted">
                    {selectedCustomer.email} • {selectedCustomer.phone}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSelectedCustomer}
                  className="text-red-400 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                <Input
                  placeholder="Search customers by name, email, or phone..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="bg-wt-bg border-wt-border text-wt-text-primary pl-10"
                />
                {searchingCustomers && (
                  <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted animate-spin" />
                )}
                
                {/* Customer search results dropdown */}
                {customerResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-wt-paper border border-wt-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {customerResults.map((customer) => (
                      <button
                        key={customer.id}
                        type="button"
                        onClick={() => selectCustomer(customer)}
                        className="w-full px-4 py-2 text-left hover:bg-wt-subtle flex items-center justify-between"
                      >
                        <div>
                          <p className="text-wt-text-primary">
                            {customer.first_name} {customer.last_name}
                          </p>
                          <p className="text-xs text-wt-text-muted">
                            {customer.email} • {customer.phone}
                          </p>
                        </div>
                        {customer.orders_count > 0 && (
                          <Badge className="bg-wt-gold/20 text-wt-gold border-0 text-xs">
                            {customer.orders_count} orders
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name" className="text-wt-text-primary">
                Customer Name *
              </Label>
              <Input
                id="customer_name"
                required
                value={formData.customer_name}
                onChange={(e) =>
                  setFormData({ ...formData, customer_name: e.target.value })
                }
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-wt-text-primary">
                Phone *
              </Label>
              <Input
                id="phone"
                required
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email" className="text-wt-text-primary">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <div>
              <Label htmlFor="source" className="text-wt-text-primary">
                Source
              </Label>
              <Select
                value={formData.source}
                onValueChange={(value) =>
                  setFormData({ ...formData, source: value })
                }
              >
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="Walk-in" className="text-wt-text-primary">Walk-in</SelectItem>
                  <SelectItem value="Website" className="text-wt-text-primary">Website</SelectItem>
                  <SelectItem value="Phone" className="text-wt-text-primary">Phone</SelectItem>
                  <SelectItem value="WhatsApp" className="text-wt-text-primary">WhatsApp</SelectItem>
                  <SelectItem value="Social Media" className="text-wt-text-primary">Social Media</SelectItem>
                  <SelectItem value="Referral" className="text-wt-text-primary">Referral</SelectItem>
                  <SelectItem value="Other" className="text-wt-text-primary">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Products Section */}
          <div className="border border-wt-border rounded-lg p-4 space-y-3">
            <Label className="text-wt-text-primary flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Products of Interest (Optional)
            </Label>

            <div className="relative">
              <Input
                placeholder="Search products by name or SKU..."
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
              {searchProduct && filteredInventory.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-wt-paper border border-wt-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredInventory.slice(0, 10).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => addProductToLead(item)}
                      className="w-full px-4 py-2 text-left hover:bg-wt-subtle flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        {item.image_url && (
                          <img 
                            src={item.image_url} 
                            alt={item.name} 
                            className="w-10 h-10 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="text-wt-text-primary text-sm">{item.name}</p>
                          <p className="text-xs text-wt-text-muted">{item.sku}</p>
                        </div>
                      </div>
                      <span className="text-wt-gold text-sm">{formatCurrency(item.selling_price)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Products */}
            {formData.products.length > 0 && (
              <div className="space-y-2 mt-3">
                {formData.products.map((product, index) => (
                  <div key={index} className="bg-wt-subtle rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {product.image_url && (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-8 h-8 object-cover rounded"
                          />
                        )}
                        <div>
                          <span className="text-wt-text-primary text-sm">{product.name}</span>
                          <span className="text-wt-text-muted text-xs ml-2">({product.sku})</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeProduct(index)}
                        className="text-red-400 hover:text-red-500 h-6 w-6 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs text-wt-text-muted">Qty</Label>
                        <Input
                          type="number"
                          min="1"
                          value={product.quantity}
                          onChange={(e) => updateProduct(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="bg-wt-bg border-wt-border text-wt-text-primary h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-wt-text-muted">Unit Price</Label>
                        <Input
                          type="number"
                          min="0"
                          value={product.unit_price}
                          onChange={(e) => updateProduct(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="bg-wt-bg border-wt-border text-wt-text-primary h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-wt-text-muted">Customization</Label>
                        <Input
                          type="number"
                          min="0"
                          value={product.customization_charge || 0}
                          onChange={(e) => updateProduct(index, 'customization_charge', parseFloat(e.target.value) || 0)}
                          className="bg-wt-bg border-wt-border text-wt-text-primary h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-wt-text-muted">Total</Label>
                        <div className="h-8 flex items-center text-wt-gold text-sm font-semibold">
                          {formatCurrency(product.total)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Additional Charges */}
          <div className="border border-wt-border rounded-lg p-4 space-y-3">
            <Label className="text-wt-text-primary flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Additional Charges & Discounts (Optional)
            </Label>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-wt-text-muted flex items-center gap-1">
                  <Truck className="h-3 w-3" />
                  Shipping Charge
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.shipping_charge}
                  onChange={(e) => setFormData({ ...formData, shipping_charge: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>

              <div>
                <Label className="text-xs text-wt-text-muted flex items-center gap-1">
                  <Percent className="h-3 w-3" />
                  Discount
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.discount_type}
                    onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                  >
                    <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary w-28">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      <SelectItem value="percentage" className="text-wt-text-primary">%</SelectItem>
                      <SelectItem value="fixed" className="text-wt-text-primary">Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                    disabled={!formData.discount_type}
                  />
                </div>
              </div>
            </div>

            {/* Custom Charges */}
            <div>
              <Label className="text-xs text-wt-text-muted mb-2 block">Custom Charges</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Charge name"
                  value={newCustomCharge.name}
                  onChange={(e) => setNewCustomCharge({ ...newCustomCharge, name: e.target.value })}
                  className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                />
                <Input
                  type="number"
                  min="0"
                  placeholder="Amount"
                  value={newCustomCharge.amount || ''}
                  onChange={(e) => setNewCustomCharge({ ...newCustomCharge, amount: parseFloat(e.target.value) || 0 })}
                  className="bg-wt-bg border-wt-border text-wt-text-primary w-28"
                />
                <Button
                  type="button"
                  onClick={addCustomCharge}
                  size="sm"
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {formData.custom_charges.length > 0 && (
                <div className="space-y-1">
                  {formData.custom_charges.map((charge, index) => (
                    <div key={index} className="flex items-center justify-between bg-wt-subtle rounded px-3 py-2">
                      <span className="text-wt-text-primary text-sm">{charge.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-wt-gold text-sm">{formatCurrency(charge.amount)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomCharge(index)}
                          className="text-red-400 hover:text-red-500 h-5 w-5 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary */}
            {(totalProductValue > 0 || shippingCharge > 0 || customChargesTotal > 0 || discountAmount > 0) && (
              <div className="border-t border-wt-border pt-3 space-y-1">
                {totalProductValue > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-wt-text-muted">Products Total:</span>
                    <span className="text-wt-text-primary">{formatCurrency(totalProductValue)}</span>
                  </div>
                )}
                {shippingCharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-wt-text-muted">Shipping:</span>
                    <span className="text-wt-text-primary">{formatCurrency(shippingCharge)}</span>
                  </div>
                )}
                {customChargesTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-wt-text-muted">Custom Charges:</span>
                    <span className="text-wt-text-primary">{formatCurrency(customChargesTotal)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Discount ({formData.discount_type === 'percentage' ? `${formData.discount_value}%` : 'Fixed'}):</span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-semibold pt-2 border-t border-wt-border">
                  <span className="text-wt-text-primary">Grand Total:</span>
                  <span className="text-wt-gold">{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="stage" className="text-wt-text-primary">
                Stage
              </Label>
              <Select
                value={formData.stage}
                onValueChange={(value) =>
                  setFormData({ ...formData, stage: value })
                }
              >
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {LEAD_STAGES.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id} className="text-wt-text-primary">
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="estimated_value" className="text-wt-text-primary">
                Estimated Value (AED) {grandTotal > 0 && <span className="text-wt-text-muted text-xs">- Auto: {formatCurrency(grandTotal)}</span>}
              </Label>
              <Input
                id="estimated_value"
                type="number"
                value={formData.estimated_value}
                onChange={(e) =>
                  setFormData({ ...formData, estimated_value: e.target.value })
                }
                placeholder={grandTotal > 0 ? `Auto: ${grandTotal}` : ''}
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes" className="text-wt-text-primary">
              Notes
            </Label>
            <textarea
              id="notes"
              rows={3}
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              className="w-full rounded-lg border border-wt-border bg-wt-bg px-3 py-2 text-sm text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-gold focus:outline-none focus:ring-1 focus:ring-wt-gold"
            />
          </div>

          {/* Shipping Address Section */}
          <div className="border border-wt-border rounded-lg p-4">
            {!showAddressFields ? (
              <button
                type="button"
                onClick={() => setShowAddressFields(true)}
                className="flex items-center gap-2 text-wt-text-muted hover:text-wt-text-primary text-sm"
              >
                <MapPin className="h-4 w-4" />
                Add Shipping/Billing Address (Optional)
              </button>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-wt-text-primary flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Shipping Address
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddressFields(false);
                      setShippingAddress({
                        address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
                      });
                      setBillingAddress({
                        address1: '', address2: '', city: '', province: '', country: 'UAE', zip: '', phone: ''
                      });
                    }}
                    className="text-wt-text-muted hover:text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs text-wt-text-muted">Address Line 1</Label>
                    <Input
                      value={shippingAddress.address1}
                      onChange={(e) => setShippingAddress(prev => ({...prev, address1: e.target.value}))}
                      className="bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-wt-text-muted">City</Label>
                    <Input
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress(prev => ({...prev, city: e.target.value}))}
                      className="bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-wt-text-muted">Country</Label>
                    <Input
                      value={shippingAddress.country}
                      onChange={(e) => setShippingAddress(prev => ({...prev, country: e.target.value}))}
                      className="bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sameAsShipping"
                    checked={sameAsShipping}
                    onChange={(e) => setSameAsShipping(e.target.checked)}
                    className="w-4 h-4 accent-wt-gold"
                  />
                  <Label htmlFor="sameAsShipping" className="text-wt-text-muted text-sm cursor-pointer">
                    Billing address same as shipping
                  </Label>
                </div>
                
                {!sameAsShipping && (
                  <div className="pt-3 border-t border-wt-border">
                    <Label className="text-wt-text-primary mb-3 block">Billing Address</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label className="text-xs text-wt-text-muted">Address Line 1</Label>
                        <Input
                          value={billingAddress.address1}
                          onChange={(e) => setBillingAddress(prev => ({...prev, address1: e.target.value}))}
                          className="bg-wt-bg border-wt-border text-wt-text-primary"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-wt-text-muted">City</Label>
                        <Input
                          value={billingAddress.city}
                          onChange={(e) => setBillingAddress(prev => ({...prev, city: e.target.value}))}
                          className="bg-wt-bg border-wt-border text-wt-text-primary"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-wt-text-muted">Country</Label>
                        <Input
                          value={billingAddress.country}
                          onChange={(e) => setBillingAddress(prev => ({...prev, country: e.target.value}))}
                          className="bg-wt-bg border-wt-border text-wt-text-primary"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-wt-text-secondary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-wt-gold text-black hover:bg-wt-gold-hover"
              data-testid="submit-lead-button"
            >
              Create Lead
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LeadsPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    new: 0,
    contacted: 0,
    quotation_sent: 0,
    negotiation: 0,
    closed_won: 0,
    closed_lost: 0
  });

  useEffect(() => {
    loadLeads();
  }, []);

  // Reset pagination when search term or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  const loadLeads = async () => {
    try {
      const data = await leadsAPI.getAll();
      setLeads(data);
      
      // Calculate tab counts
      const counts = {
        all: data.length,
        new: data.filter(l => l.stage === 'new').length,
        contacted: data.filter(l => l.stage === 'contacted').length,
        quotation_sent: data.filter(l => l.stage === 'quotation_sent').length,
        negotiation: data.filter(l => l.stage === 'negotiation').length,
        closed_won: data.filter(l => l.stage === 'closed_won').length,
        closed_lost: data.filter(l => l.stage === 'closed_lost').length
      };
      setTabCounts(counts);
    } catch (error) {
      console.error('Failed to load leads:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  // Filter leads based on active tab and search term - memoized
  const filteredLeads = useMemo(() => {
    let filtered = leads;
    
    // Filter by stage tab
    if (activeTab !== 'all') {
      filtered = leads.filter(l => l.stage === activeTab);
    }
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(l => 
        l.customer_name?.toLowerCase().includes(search) ||
        l.phone?.toLowerCase().includes(search) ||
        l.email?.toLowerCase().includes(search) ||
        l.assigned_to_name?.toLowerCase().includes(search)
      );
    }
    
    // Sort by updated date (most recent first)
    return filtered.sort((a, b) => 
      new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
    );
  }, [leads, activeTab, searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredLeads.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const getStageBadge = (stage) => {
    const stageConfig = LEAD_STAGES.find(s => s.id === stage);
    if (!stageConfig) return null;
    
    return (
      <Badge className={`${stageConfig.color} text-white border-0`}>
        {stageConfig.label}
      </Badge>
    );
  };

  // Get first product image from lead
  const getProductImage = (lead) => {
    if (!lead.products || lead.products.length === 0) return null;
    
    // Find first product with an image
    const productWithImage = lead.products.find(p => p.image_url);
    return productWithImage?.image_url || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-playfair text-3xl sm:text-4xl font-bold text-wt-text-primary mb-2">
            Leads & Sales Pipeline
          </h1>
          <p className="text-wt-text-secondary">Manage your customer journey</p>
        </div>
        <CreateLeadDialog onSuccess={loadLeads} />
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-wt-text-muted" />
          <input
            type="text"
            placeholder="Search by name, phone, email or assigned user..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-10 rounded-lg border border-wt-border bg-wt-bg pl-10 pr-4 text-sm text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-gold focus:outline-none focus:ring-1 focus:ring-wt-gold"
          />
        </div>
      </div>

      {/* Stage Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-wt-subtle border border-wt-border p-1 h-auto flex-wrap">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black px-3 py-2"
          >
            All
            <Badge className="ml-2 bg-wt-border text-wt-text-primary text-xs">{tabCounts.all}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="new" 
            className="data-[state=active]:bg-blue-500 data-[state=active]:text-white px-3 py-2"
          >
            New
            <Badge className="ml-2 bg-wt-border text-wt-text-primary text-xs">{tabCounts.new}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="contacted" 
            className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black px-3 py-2"
          >
            Contacted
            <Badge className="ml-2 bg-wt-border text-wt-text-primary text-xs">{tabCounts.contacted}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="quotation_sent" 
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white px-3 py-2"
          >
            Quotation Sent
            <Badge className="ml-2 bg-wt-border text-wt-text-primary text-xs">{tabCounts.quotation_sent}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="negotiation" 
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white px-3 py-2"
          >
            Negotiation
            <Badge className="ml-2 bg-wt-border text-wt-text-primary text-xs">{tabCounts.negotiation}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="closed_won" 
            className="data-[state=active]:bg-green-500 data-[state=active]:text-white px-3 py-2"
          >
            Won
            <Badge className="ml-2 bg-wt-border text-wt-text-primary text-xs">{tabCounts.closed_won}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="closed_lost" 
            className="data-[state=active]:bg-red-500 data-[state=active]:text-white px-3 py-2"
          >
            Lost
            <Badge className="ml-2 bg-wt-border text-wt-text-primary text-xs">{tabCounts.closed_lost}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Leads Table */}
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-wt-subtle border-b border-wt-border">
              <tr>
                <th className="text-left p-4 text-wt-text-muted font-medium">Customer</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Contact</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Assigned To</th>
                <th className="text-right p-4 text-wt-text-muted font-medium">Value</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Products</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Stage</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Source</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-wt-text-muted">
                    {searchTerm ? 'No leads found matching your search' : 'No leads in this stage'}
                  </td>
                </tr>
              ) : (
                paginatedLeads.map((lead, index) => (
                  <tr 
                    key={lead.id} 
                    className={`border-b border-wt-border/50 hover:bg-wt-subtle/50 cursor-pointer ${index % 2 === 0 ? 'bg-wt-bg/30' : ''}`}
                    onClick={() => handleViewDetails(lead.id)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td className="p-4">
                      <div>
                        <p className="text-wt-text-primary font-medium">{lead.customer_name}</p>
                        <p className="text-xs text-wt-text-muted">
                          {formatDate(lead.created_at)}
                        </p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-wt-text-primary text-sm">
                          <Phone className="h-3 w-3 text-wt-text-muted" />
                          {lead.phone}
                        </div>
                        {lead.email && (
                          <div className="flex items-center gap-1 text-wt-text-muted text-xs">
                            <Mail className="h-3 w-3" />
                            {lead.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      {lead.assigned_to_name ? (
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-wt-gold/20 flex items-center justify-center">
                            <User className="h-4 w-4 text-wt-gold" />
                          </div>
                          <span className="text-wt-text-primary text-sm">{lead.assigned_to_name}</span>
                        </div>
                      ) : (
                        <span className="text-wt-text-muted text-sm italic">Unassigned</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {lead.estimated_value ? (
                        <span className="text-wt-gold font-semibold">{formatCurrency(lead.estimated_value)}</span>
                      ) : (
                        <span className="text-wt-text-muted">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      {lead.products && lead.products.length > 0 ? (
                        <div className="flex items-center justify-center gap-2">
                          {getProductImage(lead) ? (
                            <img 
                              src={getProductImage(lead)} 
                              alt="Product" 
                              className="w-10 h-10 object-cover rounded border border-wt-border"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-wt-subtle rounded border border-wt-border flex items-center justify-center">
                              <Package className="h-5 w-5 text-wt-text-muted" />
                            </div>
                          )}
                          <div className="text-left">
                            <span className="text-purple-400 font-medium">{lead.products.length}</span>
                            <p className="text-xs text-wt-text-muted">
                              {lead.products.length === 1 ? 'item' : 'items'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-wt-text-muted text-center block">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {getStageBadge(lead.stage)}
                    </td>
                    <td className="p-4">
                      <span className="text-wt-text-secondary text-sm">{lead.source || '-'}</span>
                    </td>
                    <td className="p-4 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(lead.id);
                        }}
                        className="border-wt-border text-wt-text-primary hover:bg-wt-subtle"
                        data-testid={`view-lead-${lead.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination & Summary Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-wt-text-muted">
        <div>
          Showing {filteredLeads.length > 0 ? startIndex + 1 : 0} - {Math.min(endIndex, filteredLeads.length)} of {filteredLeads.length} leads
          {filteredLeads.length !== leads.length && (
            <span className="text-wt-text-muted"> (filtered from {leads.length} total)</span>
          )}
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="border-wt-border text-wt-text-primary hover:bg-wt-subtle disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center gap-1">
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-wt-text-muted">...</span>
                ) : (
                  <Button
                    key={page}
                    size="sm"
                    variant={currentPage === page ? 'default' : 'outline'}
                    onClick={() => handlePageClick(page)}
                    className={currentPage === page 
                      ? 'bg-wt-gold text-black hover:bg-wt-gold-hover' 
                      : 'border-wt-border text-wt-text-primary hover:bg-wt-subtle'
                    }
                  >
                    {page}
                  </Button>
                )
              ))}
            </div>
            
            <Button
              size="sm"
              variant="outline"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="border-wt-border text-wt-text-primary hover:bg-wt-subtle disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        <div className="flex items-center gap-4">
          <span>Total Pipeline Value: <span className="text-wt-gold font-semibold">
            {formatCurrency(leads.filter(l => l.stage !== 'closed_lost').reduce((sum, l) => sum + (l.estimated_value || 0), 0))}
          </span></span>
        </div>
      </div>
    </div>
  );
};

export default LeadsPage;
